import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { safeStorage } from "../lib/storage";

const KEY = "ttp-cookie-consent";

const defaultPrefs = { necessary: true, analytics: false, marketing: false };

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [state, setState] = useState(() => {
    const raw = safeStorage.get(KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return { decided: true, prefs: { ...defaultPrefs, ...parsed } };
      } catch (_) {}
    }
    return { decided: false, prefs: defaultPrefs };
  });

  const persist = (prefs) => {
    setState({ decided: true, prefs });
    try {
      safeStorage.set(KEY, JSON.stringify(prefs));
    } catch (_) {}
  };

  // Gate analytics: only initialise once consent has been given for analytics.
  useEffect(() => {
    if (state.decided && state.prefs.analytics && typeof window !== "undefined") {
      try {
        if (typeof window.__ttpInitAnalytics === "function") {
          window.__ttpInitAnalytics();
        }
      } catch (_) {}
    }
  }, [state.decided, state.prefs.analytics]);

  const acceptAll = () => persist({ necessary: true, analytics: true, marketing: true });
  const necessaryOnly = () => persist({ necessary: true, analytics: false, marketing: false });
  const savePreferences = (next) => persist({ ...defaultPrefs, ...next, necessary: true });
  const reopen = () => setState((s) => ({ ...s, decided: false }));

  const value = useMemo(
    () => ({ ...state, acceptAll, necessaryOnly, savePreferences, reopen }),
    [state]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used inside <ConsentProvider>");
  return ctx;
}
